import React, { useState } from 'react';
import { motion } from "framer-motion";
import {
  GraduationCap,
  FileText,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────
// Throws on non-JSON responses so callers always get a real Error object.
const safeJson = async (resp) => {
  const contentType = resp.headers?.get("content-type") || "";
  if (contentType.includes("application/json")) return resp.json();
  const text = await resp.text();
  throw new Error(
    /^\s*</.test(text)
      ? "A server error occurred (non-JSON response)"
      : text || "Unknown server error"
  );
};

// ── Static data ──────────────────────────────────────────────────────────────
const APPLICATION_STEPS = [
  { title: "Fill Application",  description: "Complete the form below.",            icon: GraduationCap, active: true },
  { title: "Attach Documents",  description: "Upload required certificates.",       icon: FileText,      active: true },
  { title: "Submission",        description: "Review and submit your application.", icon: CheckCircle,   active: true },
];

const DOC_FIELDS = [
  { name: 'incomeCertificate', label: 'Income Certificate' },
  { name: 'academicRecords',   label: 'Academic Records / Marksheets' },
  { name: 'idProof',           label: 'ID Proof (Aadhaar / PAN / Passport)' },
  { name: 'bankDetails',       label: 'Bank Passbook / Statement' },
];

const EMPTY_FORM = {
  fullName: '', dateOfBirth: '', gender: '', email: '', phone: '',
  address: '', city: '', state: '', pincode: '',
  guardianName: '', guardianRelation: '', guardianOccupation: '',
  guardianPhone: '', guardianEmail: '', guardianAnnualIncome: '',
  currentSchool: '', currentClass: '', currentBoard: '',
  previousYearPercentage: '', stream: '',
  incomeCertificate: null, academicRecords: null, idProof: null, bankDetails: null,
};

const MAX_FILE_SIZE   = 5 * 1024 * 1024;
const ALLOWED_TYPES   = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

// ── Pure validation ──────────────────────────────────────────────────────────
const validateForm = (fd) => {
  const e = {};

  // Student info
  if (!fd.fullName?.trim()) e.fullName = "Full Name is required.";
  if (!fd.dateOfBirth) {
    e.dateOfBirth = "Date of Birth is required.";
  } else {
    const dob   = new Date(fd.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
    if (dob > today)           e.dateOfBirth = "Date of Birth cannot be in the future.";
    else if (age < 5 || age > 30) e.dateOfBirth = "Age must be between 5 and 30.";
  }
  if (!fd.gender) e.gender = "Gender is required.";

  // Contact
  if (!fd.email)                                          e.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fd.email)) e.email = "Invalid email format.";
  if (!fd.phone)                                          e.phone = "Phone number is required.";
  else if (!/^\d{10}$/.test(fd.phone))                   e.phone = "Phone must be exactly 10 digits.";
  if (!fd.address?.trim()) e.address = "Address is required.";
  if (!fd.city?.trim())    e.city    = "City is required.";
  if (!fd.state)           e.state   = "State is required.";
  if (!fd.pincode)                          e.pincode = "Pincode is required.";
  else if (!/^\d{6}$/.test(fd.pincode))    e.pincode = "Pincode must be 6 digits.";

  // Academic
  if (!fd.currentSchool?.trim()) e.currentSchool = "Current School / College is required.";
  if (!fd.currentClass)          e.currentClass  = "Current Class / Year is required.";
  if (!fd.currentBoard)          e.currentBoard  = "Board / University is required.";
  const perc = Number(fd.previousYearPercentage);
  if (fd.previousYearPercentage === "" || fd.previousYearPercentage == null) {
    e.previousYearPercentage = "Previous Year Percentage is required.";
  } else if (isNaN(perc) || perc < 0 || perc > 100) {
    e.previousYearPercentage = "Percentage must be 0–100.";
  }

  // Guardian
  if (!fd.guardianName?.trim())       e.guardianName       = "Guardian Name is required.";
  if (!fd.guardianRelation)           e.guardianRelation   = "Guardian Relation is required.";
  if (!fd.guardianOccupation?.trim()) e.guardianOccupation = "Guardian Occupation is required.";
  if (!fd.guardianAnnualIncome)       e.guardianAnnualIncome = "Guardian Annual Income is required.";
  if (!fd.guardianPhone)                              e.guardianPhone = "Guardian phone is required.";
  else if (!/^\d{10}$/.test(fd.guardianPhone))       e.guardianPhone = "Guardian phone must be 10 digits.";
  if (fd.guardianEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fd.guardianEmail)) {
    e.guardianEmail = "Invalid guardian email format.";
  }

  // Documents
  if (!fd.incomeCertificate) e.incomeCertificate = "Income certificate required.";
  if (!fd.academicRecords)   e.academicRecords   = "Academic records required.";
  if (!fd.idProof)           e.idProof           = "ID Proof required.";
  if (!fd.bankDetails)       e.bankDetails       = "Bank Passbook required.";

  return e;
};

// ── Component ────────────────────────────────────────────────────────────────
const StudentApplicationSection = () => {
  const [formData,       setFormData]       = useState(EMPTY_FORM);
  const [errors,         setErrors]         = useState({});
  const [isSubmitting,   setIsSubmitting]   = useState(false);
  const [submitSuccess,  setSubmitSuccess]  = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({ ...prev, [name]: "File must be ≤ 5 MB." }));
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setErrors(prev => ({ ...prev, [name]: "File must be a PDF / JPG / PNG." }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: file }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Uploads a file via the /api/upload-file endpoint and returns the storage path.
  const uploadFile = async (file, folder) => {
    if (!file) return null;
    try {
      const filename = `${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
      const base64   = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload  = () => resolve(reader.result);
        reader.onerror = reject;
      });

      const response = await fetch('/api/upload-file', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ file: base64, folder, filename }),
      });

      if (!response.ok) {
        const errData = await safeJson(response);
        throw new Error(errData.details || errData.error || 'Upload failed');
      }
      const data = await response.json();
      return data.path;
    } catch (err) {
      // Surface the upload error on the submit banner; return null so the
      // file-path validation below can catch and abort submission.
      setErrors(prev => ({
        ...prev,
        submit: err.message || "File upload failed. Please try again or contact support.",
      }));
      return null;
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(prev => ({
        ...prev,
        ...validationErrors,
        submit: "Please fill all required fields correctly.",
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrors({});

    try {
      // Upload all four documents in parallel
      const [incomeCertificatePath, academicRecordsPath, idProofPath, bankDetailsPath] =
        await Promise.all([
          uploadFile(formData.incomeCertificate, 'incomeCertificates'),
          uploadFile(formData.academicRecords,   'academicRecords'),
          uploadFile(formData.idProof,           'idProofs'),
          uploadFile(formData.bankDetails,       'bankDetails'),
        ]);

      // Abort if any required upload silently failed
      const uploads = [
        { file: formData.incomeCertificate, path: incomeCertificatePath, name: 'Income certificate' },
        { file: formData.academicRecords,   path: academicRecordsPath,   name: 'Academic records' },
        { file: formData.idProof,           path: idProofPath,           name: 'ID proof' },
        { file: formData.bankDetails,       path: bankDetailsPath,       name: 'Bank details' },
      ];
      for (const { file, path, name } of uploads) {
        if (file && !path) throw new Error(`${name} upload failed`);
      }

      // Build payload — replace File objects with storage paths
      const applicationData = {
        ...formData,
        incomeCertificatePath,
        academicRecordsPath,
        idProofPath,
        bankDetailsPath,
        incomeCertificate: undefined,
        academicRecords:   undefined,
        idProof:           undefined,
        bankDetails:       undefined,
      };

      // POST to API
      const response = await fetch('/api/student-application', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(applicationData),
      });

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        const preview = (await response.text()).substring(0, 200);
        console.error('Non-JSON response from /api/student-application:', {
          status: response.status,
          statusText: response.statusText,
          preview,
        });
        throw new Error('Server error. Please try again later or contact info@meenamma-trust.org');
      }
      const result = await response.json();

      if (!result.success) throw new Error(result.error || 'Failed to submit application');

      // ── Success path ────────────────────────────────────────────────────
      const email = formData.email;
      setSubmittedEmail(email);
      setFormData(EMPTY_FORM);
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSubmitSuccess(false), 8000);

      alert(
        `✅ Application Submitted Successfully!\n\n` +
        `Confirmation emails have been sent to:\n` +
        `• Your email: ${email}\n` +
        `• Our office: info@meenamma-trust.org\n\n` +
        `NEXT STEPS:\n` +
        `Please email these documents to info@meenamma-trust.org:\n` +
        `• Income Certificate\n• Academic Records\n• ID Proof (Aadhaar)\n• Bank Passbook\n\n` +
        `Review time: 2–4 weeks\n\nThank you for applying!`
      );

    } catch (error) {
      console.error('Submission error:', error);
      const msg = error.message || "Application submission failed";
      setErrors(prev => ({ ...prev, submit: msg }));
      alert(`❌ Submission Failed\n\nError: ${msg}\n\nPlease try again or contact info@meenamma-trust.org`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <section id="scholarships" className="py-20 md:py-28 px-4 mesh-gradient">
      <div className="container mx-auto max-w-6xl">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
            Scholarship Application
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Apply for financial assistance to continue your education journey
          </p>
        </motion.div>

        {/* ── Steps ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10"
        >
          {APPLICATION_STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className={`relative p-6 rounded-xl ${
                  step.active
                    ? "bg-gradient-to-br from-brand-indigo to-brand-emerald text-white shadow-xl"
                    : "bg-white text-muted-foreground shadow-md"
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${step.active ? "bg-white/20" : "bg-brand-indigo/15"}`}>
                    <Icon className={step.active ? "text-white" : "text-blue-600"} size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{step.title}</h3>
                    <p className={`text-sm ${step.active ? "text-white/90" : "text-muted-foreground"}`}>
                      {step.description}
                    </p>
                  </div>
                </div>
                {index < APPLICATION_STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-0.5 bg-gray-300" />
                )}
              </div>
            );
          })}
        </motion.div>

        {/* ── Required documents notice ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 p-6 bg-brand-amber/10 border-2 border-brand-amber/30 rounded-xl"
        >
          <div className="flex items-start gap-4">
            <AlertCircle className="text-brand-amber flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="font-bold text-foreground text-lg mb-2">Required Documents</h3>
              <p className="text-muted-foreground mb-3">
                After filling this form, please email the following documents to{" "}
                <span className="font-semibold">info@meenamma-trust.org</span>:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Income Certificate</li>
                <li>Academic Records (Previous year mark sheets)</li>
                <li>ID Proof (Aadhaar Card copy)</li>
                <li>Bank Passbook (First page copy)</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* ── Form card ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="glassmorphism-card border border-border/60 rounded-2xl shadow-xl p-8 md:p-12"
        >

          {/* Success banner */}
          {submitSuccess && (
            <div className="mb-8 p-5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-800 dark:text-emerald-200">
                  Application Submitted Successfully!
                </p>
                <p className="text-sm text-emerald-700 dark:text-emerald-300 mt-0.5">
                  Confirmation email sent to <strong>{submittedEmail}</strong>.
                  Please also email your documents to info@meenamma-trust.org.
                </p>
              </div>
            </div>
          )}

          {/* Error banner */}
          {errors.submit && (
            <div className="mb-8 p-5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800 dark:text-red-200">Submission Failed</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">
                  {errors.submit} — If this persists, contact{" "}
                  <a href="mailto:info@meenamma-trust.org" className="underline">
                    info@meenamma-trust.org
                  </a>
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-10">

            {/* ── Student Information ── */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text flex items-center gap-2">
                <GraduationCap size={26} className="text-blue-600" />
                Student Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="fullName" value={formData.fullName}
                    onChange={handleInputChange}
                    className={`field-base ${errors.fullName ? "field-error" : ""}`}
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date" name="dateOfBirth" value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className={`field-base ${errors.dateOfBirth ? "field-error" : ""}`}
                  />
                  {errors.dateOfBirth && <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="gender" value={formData.gender} onChange={handleInputChange}
                    className={`field-base ${errors.gender ? "field-error" : ""}`}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
                </div>

              </div>
            </div>

            {/* ── Parent / Guardian Details ── */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text">
                Parent / Guardian Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Guardian's Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="guardianName" value={formData.guardianName}
                    onChange={handleInputChange}
                    className={`field-base ${errors.guardianName ? "field-error" : ""}`}
                    placeholder="Guardian's full name"
                  />
                  {errors.guardianName && <p className="text-red-500 text-sm mt-1">{errors.guardianName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Guardian Relation <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="guardianRelation" value={formData.guardianRelation}
                    onChange={handleInputChange}
                    className={`field-base ${errors.guardianRelation ? "field-error" : ""}`}
                  >
                    <option value="">Select relation</option>
                    <option value="Father">Father</option>
                    <option value="Mother">Mother</option>
                    <option value="Legal Guardian">Legal Guardian</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.guardianRelation && <p className="text-red-500 text-sm mt-1">{errors.guardianRelation}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Guardian's Occupation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="guardianOccupation" value={formData.guardianOccupation}
                    onChange={handleInputChange}
                    className={`field-base ${errors.guardianOccupation ? "field-error" : ""}`}
                    placeholder="Enter occupation"
                  />
                  {errors.guardianOccupation && <p className="text-red-500 text-sm mt-1">{errors.guardianOccupation}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Annual Income (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number" name="guardianAnnualIncome" value={formData.guardianAnnualIncome}
                    onChange={handleInputChange}
                    className={`field-base ${errors.guardianAnnualIncome ? "field-error" : ""}`}
                    placeholder="Annual income in rupees" min="0"
                  />
                  {errors.guardianAnnualIncome && <p className="text-red-500 text-sm mt-1">{errors.guardianAnnualIncome}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Guardian's Email
                  </label>
                  <input
                    type="email" name="guardianEmail" value={formData.guardianEmail}
                    onChange={handleInputChange}
                    className={`field-base ${errors.guardianEmail ? "field-error" : ""}`}
                    placeholder="guardian@example.com"
                  />
                  {errors.guardianEmail && <p className="text-red-500 text-sm mt-1">{errors.guardianEmail}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Guardian Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel" name="guardianPhone" value={formData.guardianPhone}
                    onChange={handleInputChange}
                    className={`field-base ${errors.guardianPhone ? "field-error" : ""}`}
                    placeholder="10-digit phone number" maxLength="10"
                  />
                  {errors.guardianPhone && <p className="text-red-500 text-sm mt-1">{errors.guardianPhone}</p>}
                </div>

              </div>
            </div>

            {/* ── Contact Information ── */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email" name="email" value={formData.email}
                    onChange={handleInputChange}
                    className={`field-base ${errors.email ? "field-error" : ""}`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel" name="phone" value={formData.phone}
                    onChange={handleInputChange}
                    className={`field-base ${errors.phone ? "field-error" : ""}`}
                    placeholder="10-digit phone number" maxLength="10"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="address" value={formData.address}
                    onChange={handleInputChange} rows={3}
                    className={`field-base ${errors.address ? "field-error" : ""}`}
                    placeholder="Enter your complete address"
                  />
                  {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="city" value={formData.city}
                    onChange={handleInputChange}
                    className={`field-base ${errors.city ? "field-error" : ""}`}
                    placeholder="City"
                  />
                  {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state" value={formData.state} onChange={handleInputChange}
                    className={`field-base ${errors.state ? "field-error" : ""}`}
                  >
                    <option value="">Select state</option>
                    <option value="AP">Andhra Pradesh</option>
                    <option value="AR">Arunachal Pradesh</option>
                    <option value="AS">Assam</option>
                    <option value="BR">Bihar</option>
                    <option value="CT">Chhattisgarh</option>
                    <option value="GA">Goa</option>
                    <option value="GJ">Gujarat</option>
                    <option value="HR">Haryana</option>
                    <option value="HP">Himachal Pradesh</option>
                    <option value="JK">Jammu and Kashmir</option>
                    <option value="JH">Jharkhand</option>
                    <option value="KA">Karnataka</option>
                    <option value="KL">Kerala</option>
                    <option value="MP">Madhya Pradesh</option>
                    <option value="MH">Maharashtra</option>
                    <option value="MN">Manipur</option>
                    <option value="ML">Meghalaya</option>
                    <option value="MZ">Mizoram</option>
                    <option value="NL">Nagaland</option>
                    <option value="OR">Odisha</option>
                    <option value="PB">Punjab</option>
                    <option value="RJ">Rajasthan</option>
                    <option value="SK">Sikkim</option>
                    <option value="TN">Tamil Nadu</option>
                    <option value="TG">Telangana</option>
                    <option value="TR">Tripura</option>
                    <option value="UP">Uttar Pradesh</option>
                    <option value="UT">Uttarakhand</option>
                    <option value="WB">West Bengal</option>
                  </select>
                  {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="pincode" value={formData.pincode}
                    onChange={handleInputChange}
                    className={`field-base ${errors.pincode ? "field-error" : ""}`}
                    placeholder="6-digit pincode" maxLength="6"
                  />
                  {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>}
                </div>

              </div>
            </div>

            {/* ── Academic Details ── */}
            <div>
              <h3 className="text-xl font-bold mb-5 gradient-text">
                Academic Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Current School / College <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text" name="currentSchool" value={formData.currentSchool}
                    onChange={handleInputChange}
                    className={`field-base ${errors.currentSchool ? "field-error" : ""}`}
                    placeholder="Name of your institution"
                  />
                  {errors.currentSchool && <p className="text-red-500 text-sm mt-1">{errors.currentSchool}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Current Class / Year <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="currentClass" value={formData.currentClass} onChange={handleInputChange}
                    className={`field-base ${errors.currentClass ? "field-error" : ""}`}
                  >
                    <option value="">Select class</option>
                    <option value="9">Class 9</option>
                    <option value="10">Class 10</option>
                    <option value="11">Class 11</option>
                    <option value="12">Class 12</option>
                    <option value="ug1">UG Year 1</option>
                    <option value="ug2">UG Year 2</option>
                    <option value="ug3">UG Year 3</option>
                    <option value="ug4">UG Year 4</option>
                    <option value="pg1">PG Year 1</option>
                    <option value="pg2">PG Year 2</option>
                  </select>
                  {errors.currentClass && <p className="text-red-500 text-sm mt-1">{errors.currentClass}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Board / University <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="currentBoard" value={formData.currentBoard} onChange={handleInputChange}
                    className={`field-base ${errors.currentBoard ? "field-error" : ""}`}
                  >
                    <option value="">Select board</option>
                    <option value="CBSE">CBSE</option>
                    <option value="ICSE">ICSE</option>
                    <option value="State Board">State Board</option>
                    <option value="IB">International Baccalaureate</option>
                    <option value="University">University</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.currentBoard && <p className="text-red-500 text-sm mt-1">{errors.currentBoard}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Previous Year Percentage <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number" name="previousYearPercentage" value={formData.previousYearPercentage}
                    onChange={handleInputChange}
                    className={`field-base ${errors.previousYearPercentage ? "field-error" : ""}`}
                    placeholder="Percentage (0–100)" min="0" max="100" step="0.01"
                  />
                  {errors.previousYearPercentage && (
                    <p className="text-red-500 text-sm mt-1">{errors.previousYearPercentage}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                    Stream (if applicable)
                  </label>
                  <select
                    name="stream" value={formData.stream} onChange={handleInputChange}
                    className="field-base"
                  >
                    <option value="">Select stream</option>
                    <option value="Science">Science</option>
                    <option value="Commerce">Commerce</option>
                    <option value="Arts">Arts / Humanities</option>
                    <option value="Engineering">Engineering</option>
                    <option value="Medical">Medical</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

              </div>
            </div>

            {/* ── Document Uploads ── */}
            <div>
              <h3 className="text-xl font-bold mb-3 gradient-text flex items-center gap-2">
                <FileText size={26} className="text-blue-600" />
                Document Uploads
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Upload all four required documents (PDF, JPG, or PNG — max 5 MB each).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {DOC_FIELDS.map(({ name, label }) => (
                  <div key={name}>
                    <label className="block text-sm font-semibold text-foreground/80 mb-1.5">
                      {label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file" id={name} name={name}
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    <label
                      htmlFor={name}
                      className={`dropzone flex-col gap-2 ${errors[name] ? "dropzone-error" : ""}`}
                    >
                      <FileText className="h-7 w-7 text-muted-foreground/50" />
                      <span className="text-sm text-muted-foreground text-center">
                        {formData[name] ? formData[name].name : "Click to upload"}
                      </span>
                    </label>
                    {errors[name] && <p className="text-red-500 text-sm mt-1">{errors[name]}</p>}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Terms ── */}
            <p className="text-center text-sm text-muted-foreground">
              By submitting this form, you agree to our terms and conditions.
              All information provided will be kept confidential.
            </p>

            {/* ── Submit button ── */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex justify-center pt-2"
            >
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white
                            font-semibold rounded-lg shadow-lg hover:from-blue-700 hover:to-indigo-700
                            focus:outline-none focus:ring-4 focus:ring-blue-300 transition
                            ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:scale-105"}`}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962
                           7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting Application...
                  </span>
                ) : (
                  'Submit Application'
                )}
              </button>
            </motion.div>

          </form>
        </motion.div>

        {/* ── Footer note ── */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-center text-muted-foreground"
        >
          <p className="flex items-center justify-center gap-2">
            <CheckCircle className="text-green-500" size={20} />
            Your information is secure and will be used solely for scholarship processing
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default StudentApplicationSection;
